import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { 
  Shield, 
  Heart, 
  Plane, 
  AlertTriangle,
  CheckCircle,
  Star,
  Phone,
  ArrowRight,
  Globe,
  Clock
} from 'lucide-react';

const InsurancePage = () => {
  const coverageTypes = [
    {
      icon: Shield,
      title: "Annulation/Interruption",
      coverage: "Jusqu'à 100% du coût",
      description: "Remboursement intégral en cas d'annulation pour raisons couvertes : maladie, accident, décès, catastrophe naturelle.",
      features: ["Annulation avant départ", "Interruption de séjour", "Retour anticipé", "Report de dates"]
    },
    {
      icon: Heart,
      title: "Assistance Médicale",
      coverage: "Frais illimités",
      description: "Prise en charge complète des frais médicaux et pharmaceutiques, rapatriement sanitaire 24h/24.",
      features: ["Consultations médicales", "Hospitalisation", "Médicaments", "Rapatriement sanitaire"]
    },
    {
      icon: Plane,
      title: "Transport & Bagages",
      coverage: "Jusqu'à 10 000€",
      description: "Indemnisation en cas de retard, perte ou vol de bagages, modification de vols.",
      features: ["Retard de transport", "Bagages perdus/volés", "Frais de séjour supplémentaire", "Objets de valeur"]
    },
    {
      icon: AlertTriangle,
      title: "Responsabilité Civile",
      coverage: "Jusqu'à 1 500 000€",
      description: "Protection contre les dommages causés à autrui ou aux biens de la villa louée.",
      features: ["Dommages matériels", "Dommages corporels", "Franchise réduite", "Assistance juridique"]
    }
  ];

  const plans = [
    {
      name: "Essentiel",
      price: "89€",
      period: "par personne",
      recommended: false,
      features: [
        "Annulation jusqu'à 75%",
        "Assistance médicale de base",
        "Bagages jusqu'à 2 000€",
        "RC jusqu'à 750 000€",
        "Support téléphonique"
      ]
    },
    {
      name: "Premium",
      price: "149€", 
      period: "par personne",
      recommended: true,
      features: [
        "Annulation jusqu'à 100%",
        "Assistance médicale illimitée",
        "Bagages jusqu'à 5 000€",
        "RC jusqu'à 1 500 000€",
        "Conciergerie d'urgence 24/7",
        "Sans franchise médicale"
      ]
    },
    {
      name: "Excellence",
      price: "249€",
      period: "par personne", 
      recommended: false,
      features: [
        "Couverture intégrale",
        "Assistance médicale premium",
        "Bagages jusqu'à 10 000€",
        "RC jusqu'à 2 000 000€",
        "Conciergerie dédiée",
        "Extension famille",
        "Activités à risques incluses"
      ]
    }
  ];

  const emergencyContacts = [
    {
      service: "Urgence Médicale",
      number: "+33 1 42 99 02 02",
      availability: "24h/24 - 365j/an"
    },
    {
      service: "Assistance Voyage",
      number: "+33 1 42 99 02 03", 
      availability: "24h/24 - 365j/an"
    },
    {
      service: "Support PRIVIA",
      number: "+1 786 840 0745",
      availability: "7j/7 - 7h30-19h30 EST"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Badge 
            variant="outline" 
            className="mb-6 px-6 py-3 bg-white/50 backdrop-blur-sm border-gray-200 text-gray-700 text-sm font-light uppercase tracking-wider"
          >
            <Shield className="w-4 h-4 mr-2" />
            Protection Voyage
          </Badge>
          
          <h1 
            className="text-5xl md:text-6xl font-light mb-6 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Assurance Voyage
          </h1>
          
          <p 
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Voyagez l'esprit tranquille avec notre couverture complète adaptée aux séjours de luxe
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-3 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
            >
              Souscrire Maintenant
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Coverage Types */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Couverture Complète
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Une protection adaptée aux voyages de prestige et aux villas d'exception
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {coverageTypes.map((coverage, index) => {
              const IconComponent = coverage.icon;
              return (
                <Card key={index} className="p-8 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-7 h-7 text-blue-600" />
                    </div>
                    <div>
                      <h3 
                        className="text-xl font-light text-gray-800 mb-2"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        {coverage.title}
                      </h3>
                      <Badge variant="outline" className="text-blue-600 border-blue-200">
                        {coverage.coverage}
                      </Badge>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {coverage.description}
                  </p>
                  
                  <div className="space-y-2">
                    {coverage.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Insurance Plans */}
      <section className="py-16 px-6 bg-white/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Formules d'Assurance
            </h2>
            <p className="text-lg text-gray-600">
              Choisissez la protection qui correspond à votre profil de voyage
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <Card key={index} className={`p-6 text-center relative ${
                plan.recommended ? 'ring-2 ring-blue-500 shadow-xl' : 'hover:shadow-lg'
              } transition-all duration-300`}>
                {plan.recommended && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white">
                    <Star className="w-3 h-3 mr-1" />
                    Recommandé
                  </Badge>
                )}
                
                <h3 
                  className="text-2xl font-light text-gray-800 mb-4"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {plan.name}
                </h3>
                
                <div className="mb-6">
                  <div className="text-4xl font-light text-blue-600 mb-1">
                    {plan.price}
                  </div>
                  <p className="text-sm text-gray-500">{plan.period}</p>
                </div>
                
                <div className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-left">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <Button 
                  className={`w-full ${
                    plan.recommended 
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  } transition-all duration-300`}
                >
                  Choisir {plan.name}
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Contacts */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Contacts d'Urgence
            </h2>
            <p className="text-lg text-gray-600">
              Assistance disponible 24h/24 depuis n'importe où dans le monde
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {emergencyContacts.map((contact, index) => (
              <Card key={index} className="p-6 text-center hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-6 h-6 text-red-600" />
                </div>
                
                <h3 className="text-lg font-medium text-gray-800 mb-2">
                  {contact.service}
                </h3>
                
                <p className="text-xl font-light text-blue-600 mb-2">
                  {contact.number}
                </p>
                
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span>{contact.availability}</span>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Card className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Globe className="w-5 h-5 text-amber-600" />
                <h3 className="text-lg font-medium text-gray-800">Application Mobile</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Téléchargez l'app PRIVIA Assistance pour un accès direct à vos contrats et contacts d'urgence
              </p>
              <div className="flex justify-center gap-4">
                <Button size="sm" variant="outline">App Store</Button>
                <Button size="sm" variant="outline">Google Play</Button>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsurancePage;